/**
 * Responsibility: shape a hollow cut into the land, from its floor out to its rim.
 *
 * ## Why this is its own module
 *
 * A river channel and a lake basin are the same operation with a different mask. Both take a floor level, the
 * surrounding land, and how far out toward the rim a coordinate sits, and blend between them — and both want a
 * flat-bottomed profile rather than a funnel. Only *where* the hollow is differs: a channel follows the zero-crossing
 * contours of a noise field, a basin follows the peaks of one.
 *
 * Keeping the profile here means the two cannot drift apart, and it makes the one genuinely surprising property of that
 * profile discoverable from one place — see `carveHollow`.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/**
 * Elevation inside a hollow.
 *
 * @param {number} land - Surrounding ground height, as if the hollow were not there.
 * @param {number} floor - Height at the very bottom of the hollow.
 * @param {number} fromRim - 0 at the hollow's deepest point, 1 at its rim, above 1 outside it.
 * @param {number} sharpness - Exponent on the profile. See below.
 * @returns {number} Elevation in world units.
 *
 * ## The sharpness exponent does two things, and the second one is the surprise
 *
 * Above 1 it holds the floor near full depth before rising, which gives a flat bottom instead of a funnel. That much is
 * obvious from the arithmetic.
 *
 * What is not obvious is that **it is also the strongest lever on how wide the water is.** Water covers the part of the
 * floor lying below the water level, so a flatter floor is a wider body of water: raising this from 1.5 to 3 took the
 * river from 37% of its valley half-width to 67%, which was the difference between a stream and a river. Anything
 * tuning the *shape* of a hollow is also tuning the *extent* of whatever fills it.
 */
export function carveHollow(land, floor, fromRim, sharpness) {
  if (fromRim >= 1) return land;
  if (fromRim <= 0) return floor;

  return floor + (land - floor) * Math.pow(fromRim, sharpness);
}
